import { useEffect, useState, type FormEvent } from "react";
import { useEngagementHeader } from "../lib/session";
import {
  ApiError,
  api,
  type AccessRecord,
  type Classification,
  type FolderKey,
  type FolderListing,
} from "../lib/api";
import { useResource } from "../lib/useResource";
import { AppShell, ScreenHead } from "../shell/AppShell";
import { Region, Entry } from "../components/Register";
import {
  AbsentNotice,
  Button,
  Empty,
  Failed,
  Field,
  Mark,
  Select,
  SkeletonRows,
  classificationTone,
} from "../components/primitives";
import { classificationLabel, folderLabel, stamp } from "../lib/format";

/** The filing cabinet: eight stable places, real file actions and an audit trail. */
export function Files({ engagementId }: { engagementId: string }) {
  const [tab, setTab] = useState<"cabinet" | "access">("cabinet");
  const [uploadTo, setUploadTo] = useState<FolderKey | null>(null);
  const [revision, setRevision] = useState(0);
  const [notice, setNotice] = useState<string | null>(null);
  const engagement = useEngagementHeader(engagementId);

  return (
    <AppShell engagement={engagement ?? undefined}>
      <ScreenHead
        title="Files"
        sub="Find the latest version and see who has accessed it."
      >
        <Button tone="primary" onClick={() => setUploadTo("01_client_documents")}>
          Upload file <span aria-hidden>→</span>
        </Button>
        <div className="flex" role="tablist" aria-label="Files views">
          {(["cabinet", "access"] as const).map((key) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={
                "border px-3 py-1.5 text-2xs uppercase tracking-chip transition-colors duration-150 " +
                (tab === key
                  ? "border-ink bg-ink text-canvas"
                  : "border-line text-secondary hover:border-line-strong hover:text-ink")
              }
            >
              {key === "cabinet" ? "Folders" : "Access history"}
            </button>
          ))}
        </div>
      </ScreenHead>

      {uploadTo && (
        <UploadPanel
          engagementId={engagementId}
          folderKey={uploadTo}
          onFolderChange={setUploadTo}
          onCancel={() => setUploadTo(null)}
          onComplete={(message) => {
            setUploadTo(null);
            setNotice(message);
            setRevision((value) => value + 1);
          }}
        />
      )}

      {notice && (
        <div
          role="status"
          className="mb-6 border border-success/40 bg-success/[0.06] px-3 py-2.5 text-xs text-success"
        >
          {notice}
        </div>
      )}

      {tab === "cabinet" ? (
        <Cabinet
          engagementId={engagementId}
          revision={revision}
          onUpload={(folder) => {
            setNotice(null);
            setUploadTo(folder);
          }}
        />
      ) : (
        <AccessLog engagementId={engagementId} />
      )}
    </AppShell>
  );
}

function Cabinet({
  engagementId,
  revision,
  onUpload,
}: {
  engagementId: string;
  revision: number;
  onUpload: (folder: FolderKey) => void;
}) {
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [previewing, setPreviewing] = useState<Preview | null>(null);
  const { resource, reload } = useResource<FolderListing[]>(
    () => api.get<FolderListing[]>(`/api/engagements/${engagementId}/folders`),
    [engagementId, revision],
  );

  async function download(versionId: string) {
    setDownloading(versionId);
    setDownloadError(null);
    try {
      const file = await api.downloadFile(`/api/document-versions/${versionId}/download`);
      const url = URL.createObjectURL(file.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      setDownloadError(
        error instanceof Error ? error.message : "The file could not be downloaded.",
      );
    } finally {
      setDownloading(null);
    }
  }


  /**
   * Read a file without taking a copy.
   *
   * Fetched rather than pointed at with an <iframe src>, for two reasons.
   * The blob URL keeps the bytes in this tab instead of making the file a
   * separately addressable URL somebody can paste elsewhere, and fetching
   * lets a 415 ("this kind of file cannot be rendered") arrive as a readable
   * message rather than as a browser download prompt appearing from nowhere.
   *
   * It is still an access and the server logs it as one — the access trail
   * records 'preview' beside 'download', so reading a client's file never
   * happens off the record.
   */
  async function preview(versionId: string, name: string) {
    setDownloading(versionId);
    setDownloadError(null);
    try {
      const file = await api.downloadFile(
        `/api/document-versions/${versionId}/preview`,
      );
      setPreviewing({
        url: URL.createObjectURL(file.blob),
        name,
        type: file.blob.type,
      });
    } catch (error) {
      setDownloadError(
        error instanceof Error
          ? error.message
          : "The file could not be previewed.",
      );
    } finally {
      setDownloading(null);
    }
  }

  function closePreview() {
    if (previewing) URL.revokeObjectURL(previewing.url);
    setPreviewing(null);
  }

  if (resource.state === "loading") return <SkeletonRows rows={8} />;
  if (resource.state === "absent") return <AbsentNotice />;
  if (resource.state === "failed") {
    return (
      <Failed
        message={resource.message}
        correlationId={resource.correlationId}
        onRetry={reload}
      />
    );
  }

  const folders = resource.data;
  if (folders.length === 0) {
    return (
      <Empty title="This engagement has no filing structure yet">
        The filing structure is created with the engagement. Ask an administrator
        to check this engagement before files are added.
      </Empty>
    );
  }

  return (
    <>
      {previewing && (
        <PreviewPanel preview={previewing} onClose={closePreview} />
      )}

      {downloadError && (
        <p
          role="alert"
          className="mb-6 border border-danger/40 bg-danger/[0.06] px-3 py-2.5 text-xs text-danger"
        >
          {downloadError}
        </p>
      )}

      {folders.map((folder) => {
        const { ordinal, name } = folderLabel(folder.folderKey);
        return (
          <Region
            key={folder.folderKey}
            title={`${ordinal} ${name}`}
            count={folder.documents.length}
            action={
              <Button tone="quiet" onClick={() => onUpload(folder.folderKey)}>
                Upload file
              </Button>
            }
          >
            {folder.documents.length === 0 ? (
              <p className="border-t border-line py-3.5 text-xs text-muted">
                No files in this folder yet. Upload a file to add the first one.
              </p>
            ) : (
              folder.documents.map((doc) => (
                <Entry
                  key={doc.id}
                  lead={doc.name}
                  meta={
                    <>
                      {doc.uploadedBy} &middot;{" "}
                      <span className="font-mono">
                        {doc.versionCount} version{doc.versionCount === 1 ? "" : "s"}
                      </span>
                    </>
                  }
                  right={
                    <>
                      <Mark tone={classificationTone(doc.classification)}>
                        {classificationLabel(doc.classification)}
                      </Mark>
                      <Button
                        tone="quiet"
                        disabled={!doc.latestVersionId}
                        onClick={() =>
                          doc.latestVersionId &&
                          void preview(doc.latestVersionId, doc.name)
                        }
                      >
                        Preview
                      </Button>
                      <Button
                        tone="quiet"
                        loading={downloading === doc.latestVersionId}
                        disabled={!doc.latestVersionId}
                        onClick={() => doc.latestVersionId && void download(doc.latestVersionId)}
                      >
                        Download
                      </Button>
                    </>
                  }
                />
              ))
            )}
          </Region>
        );
      })}
    </>
  );
}

const FOLDERS: FolderKey[] = [
  "01_client_documents",
  "02_research",
  "03_working_files",
  "04_draft_deliverables",
  "05_final_deliverables",
  "06_commercial",
  "07_communications",
  "08_meetings_transcripts",
];

function UploadPanel({
  engagementId,
  folderKey,
  onFolderChange,
  onCancel,
  onComplete,
}: {
  engagementId: string;
  folderKey: FolderKey;
  onFolderChange: (folder: FolderKey) => void;
  onCancel: () => void;
  onComplete: (message: string) => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [classification, setClassification] = useState<Classification>("internal");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!file) {
      setError("Choose the file you want to upload.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await api.uploadFile(
        `/api/engagements/${engagementId}/folders/${folderKey}/files` +
          `?name=${encodeURIComponent(file.name)}&classification=${classification}`,
        file,
      );
      onComplete(`${file.name} was added to ${folderLabel(folderKey).name}.`);
    } catch (caught) {
      const reference =
        caught instanceof ApiError && caught.correlationId
          ? ` Reference ${caught.correlationId}.`
          : "";
      setError(
        `${caught instanceof Error ? caught.message : "The file could not be uploaded."}${reference}`,
      );
    } finally {
      setSaving(false);
    }
  }

  return (
    <section
      aria-labelledby="upload-title"
      className="mb-8 border-y border-line-strong bg-panel px-4 py-5 sm:px-5"
    >
      <div className="mb-5 flex items-start justify-between gap-6">
        <div>
          <h2
            id="upload-title"
            className="font-display text-xl font-medium tracking-title text-ink"
          >
            Add a file
          </h2>
          <p className="mt-1 text-xs text-muted">
            Choose where it belongs and who may see it. Maximum file size: 25 MB.
          </p>
        </div>
        <Button tone="quiet" type="button" onClick={onCancel}>
          Close
        </Button>
      </div>

      <form onSubmit={submit} className="grid gap-4 lg:grid-cols-3 lg:items-end">
        <Field
          label="File"
          type="file"
          required
          onChange={(event) => setFile(event.currentTarget.files?.[0] ?? null)}
        />
        <Select
          label="Folder"
          value={folderKey}
          onChange={(event) => onFolderChange(event.currentTarget.value as FolderKey)}
        >
          {FOLDERS.map((key) => {
            const folder = folderLabel(key);
            return (
              <option key={key} value={key}>
                {folder.ordinal} {folder.name}
              </option>
            );
          })}
        </Select>
        <Select
          label="Who can see it"
          value={classification}
          onChange={(event) =>
            setClassification(event.currentTarget.value as Classification)
          }
        >
          <option value="client_visible">Client visible</option>
          <option value="internal">Internal team</option>
          <option value="restricted">Restricted</option>
          <option value="highly_restricted">Highly restricted</option>
        </Select>

        {error && (
          <p role="alert" className="text-xs text-danger lg:col-span-3">
            {error}
          </p>
        )}

        <div className="flex gap-3 lg:col-span-3">
          <Button tone="primary" type="submit" loading={saving}>
            Upload file
          </Button>
          <Button tone="secondary" type="button" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        </div>
      </form>
    </section>
  );
}

/** Every successful download is visible here; the list itself is immutable. */
function AccessLog({ engagementId }: { engagementId: string }) {
  const { resource, reload } = useResource<AccessRecord[]>(
    () => api.get<AccessRecord[]>(`/api/engagements/${engagementId}/file-access`),
    [engagementId],
  );

  if (resource.state === "loading") return <SkeletonRows rows={6} />;
  if (resource.state === "absent") return <AbsentNotice />;
  if (resource.state === "failed") {
    return (
      <Failed
        message={resource.message}
        correlationId={resource.correlationId}
        onRetry={reload}
      />
    );
  }

  const log = resource.data;
  return (
    <Region title="Access history" count={log.length}>
      {log.length === 0 ? (
        <Empty title="No file has been downloaded yet">
          When someone downloads a file you will see who, what and when here.
          This list cannot be edited.
        </Empty>
      ) : (
        log.map((entry, i) => (
          <Entry
            key={`${entry.objectId}-${entry.at}-${i}`}
            lead={
              <>
                <span className="text-secondary">{entry.fullName}</span> took{" "}
                {entry.objectType === "document_version" ? "a document" : "a deliverable"}
              </>
            }
            meta={
              <>
                {stamp(entry.at)}
                {entry.purpose && <> &middot; &ldquo;{entry.purpose}&rdquo;</>}
              </>
            }
          />
        ))
      )}
    </Region>
  );
}

interface Preview {
  url: string;
  name: string;
  type: string;
}

/**
 * The preview itself.
 *
 * **Why this is not one sandboxed iframe for everything.** It was, and that
 * is exactly why PDFs showed an empty frame: `sandbox` blocks plugin-class
 * content, and Chrome renders PDFs with a plugin-class viewer. So the single
 * most common document a firm previews was the one kind that could not be.
 *
 * What made the blanket sandbox necessary was not knowing what the bytes
 * were — the stored MIME type is whatever the uploader's Content-Type header
 * claimed. The server now reads the file's signature and refuses to serve
 * anything inline whose bytes disagree with its label, so the type here is
 * established rather than asserted. Each kind then gets the element that
 * actually renders it:
 *
 *   images  <img>, which cannot execute anything whatever the bytes are
 *   text    <pre>, inert by construction
 *   pdf     <iframe>, because that is what the viewer needs
 *
 * An HTML file mislabelled as a PDF never reaches this component: the server
 * returns 415 and the caller shows the message.
 */
function PreviewPanel({
  preview,
  onClose,
}: {
  preview: Preview;
  onClose: () => void;
}) {
  const kind = preview.type.split(";", 1)[0]!.trim().toLowerCase();

  return (
    <div className="mb-6 border border-line-strong">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-2.5">
        <p className="min-w-0 truncate text-sm text-ink">{preview.name}</p>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted">
            This view is recorded in the access history
          </span>
          <a
            href={preview.url}
            target="_blank"
            rel="noreferrer noopener"
            className="text-xs text-secondary underline-offset-4 hover:text-ink hover:underline"
          >
            Open full size
          </a>
          <Button tone="quiet" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>

      {kind.startsWith("image/") ? (
        <div className="flex max-h-[70vh] justify-center overflow-auto bg-white p-4">
          <img
            src={preview.url}
            alt={preview.name}
            className="max-w-full object-contain"
          />
        </div>
      ) : kind.startsWith("text/") ? (
        <TextPreview url={preview.url} />
      ) : (
        <iframe
          title={`Preview of ${preview.name}`}
          src={preview.url}
          className="h-[70vh] w-full bg-white"
        />
      )}
    </div>
  );
}

/**
 * Text, read from the blob and printed.
 *
 * Rendered as text in a <pre> rather than handed to the browser as a
 * document, so there is no path by which it could be interpreted as markup.
 */
function TextPreview({ url }: { url: string }) {
  const [text, setText] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    fetch(url)
      .then((r) => r.text())
      .then((t) => live && setText(t.slice(0, 200_000)))
      .catch(() => live && setText("This file could not be read as text."));
    return () => {
      live = false;
    };
  }, [url]);

  return (
    <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap break-words bg-white p-4 font-mono text-xs text-ink">
      {text ?? "Loading…"}
    </pre>
  );
}
